import { escapeRegExp, isEmpty } from 'lodash';

import { SearchCriterion, SearchQuery } from '@neptune/search-query-domain';
import { isValidRegExp } from '@neptune/shared/common-util';
import { AttributeNameFilter } from '@neptune/shared/search-domain';

import { queryMapper } from './query-mapper';
import { SearchQueryTermType, SearchQueryTermWithoutPositions } from './types';

export const createSearchModelConverter = () => {
  function fromSearchQueryToAttributeNameFilter(
    searchQuery: SearchQueryTermWithoutPositions[],
  ): Pick<AttributeNameFilter, 'mustMatchAny'> {
    if (isEmpty(searchQuery)) {
      return createEmptyAttributeFilter();
    }

    const parsedQuery = queryMapper.mapRegexQueryToSearchQuery(searchQuery);
    const mustMatchAny = convertSearchQueryToGroups(parsedQuery);

    if (mustMatchAny.length === 0) {
      return createEmptyAttributeFilter();
    }

    return { mustMatchAny };
  }

  function fromSearchQueryToSingleAttributeNameFilter(
    searchQuery: SearchQueryTermWithoutPositions[],
  ) {
    if (isEmpty(searchQuery)) {
      return createEmptyAttributeFilter();
    }

    const firstCriterion = searchQuery.find((term) => term.type === SearchQueryTermType.CRITERION);

    if (!firstCriterion) {
      return createEmptyAttributeFilter();
    }

    return {
      mustMatchAny: [
        {
          mustMatchRegexes: [escapeRegExp(firstCriterion.value)],
          mustNotMatchRegexes: [],
        },
      ],
    };
  }

  function fromDomainToTitle(terms: SearchQueryTermWithoutPositions[]): string {
    return terms.map((criterion) => criterion.value).join(' ');
  }

  return {
    fromSearchQueryToAttributeNameFilter,
    fromDomainToTitle,
    fromSearchQueryToSingleAttributeNameFilter,
  };
};

function createEmptyAttributeFilter(): Pick<AttributeNameFilter, 'mustMatchAny'> {
  return {
    mustMatchAny: [
      {
        mustMatchRegexes: [],
        mustNotMatchRegexes: [],
      },
    ],
  };
}

function isValidValue(value?: string) {
  return value && isValidRegExp(value);
}

export const searchModelConverter = createSearchModelConverter();

function convertSearchQueryToGroups(searchQuery: SearchQuery) {
  const groups = toGroups(searchQuery);
  return groups.filter(
    (group) => group.mustMatchRegexes.length > 0 || group.mustNotMatchRegexes.length > 0,
  );
}

type AttributeGroup = {
  mustMatchRegexes: string[];
  mustNotMatchRegexes: string[];
};

function toGroups(node: SearchQuery | SearchCriterion): AttributeGroup[] {
  if ('criteria' in node) {
    if (node.criteria.length === 0) {
      return [];
    }

    if (node.operator === 'or') {
      return node.criteria.flatMap((criterion) => toGroups(criterion));
    }

    return node.criteria.reduce<AttributeGroup[]>((acc, criterion) => {
      const nextGroups = toGroups(criterion);

      if (acc.length === 0) {
        return nextGroups;
      }

      if (nextGroups.length === 0) {
        return acc;
      }

      const merged: AttributeGroup[] = [];

      acc.forEach((leftGroup) => {
        nextGroups.forEach((rightGroup) => {
          merged.push({
            mustMatchRegexes: [...leftGroup.mustMatchRegexes, ...rightGroup.mustMatchRegexes],
            mustNotMatchRegexes: [
              ...leftGroup.mustNotMatchRegexes,
              ...rightGroup.mustNotMatchRegexes,
            ],
          });
        });
      });

      return merged;
    }, []);
  }

  if (node.type !== 'string' || !('value' in node) || !isValidValue(node.value)) {
    return [];
  }

  if (node.operator === '!matches') {
    return [{ mustMatchRegexes: [], mustNotMatchRegexes: [node.value] }];
  }

  if (node.operator === 'matches') {
    return [{ mustMatchRegexes: [node.value], mustNotMatchRegexes: [] }];
  }

  return [];
}
